
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { BlogPost } from '@/components/blog/types';

export const useBlogState = (blogPosts: BlogPost[]) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);

  // Ensure blogPosts is an array
  const safeBlogPosts = Array.isArray(blogPosts) ? blogPosts : [];

  // Handle post click
  const handlePostClick = (post: BlogPost) => {
    setSelectedPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/blog?post=${post.id}`, { replace: true });
  };

  // Handle back to list
  const handleBackClick = () => {
    setSelectedPost(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/blog', { replace: true });
  };

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    
    if (query.length > 0) {
      const filtered = safeBlogPosts.filter(post => 
        post.title.toLowerCase().includes(query) || 
        post.excerpt.toLowerCase().includes(query) || 
        (post.category && post.category.toLowerCase().includes(query))
      );
      setFilteredPosts(filtered);
    } else {
      setFilteredPosts(safeBlogPosts);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredPosts(safeBlogPosts);
  };

  // Check URL params on mount and handle popstate events
  useEffect(() => {
    const loadPostFromUrl = () => {
      const postId = searchParams.get('post');
      
      if (postId) {
        const post = safeBlogPosts.find(p => p.id === postId);
        if (post) {
          setSelectedPost(post);
        } else {
          // If post not found, redirect to blog listing
          setSelectedPost(null);
        }
      } else {
        setSelectedPost(null);
      }
    };

    // Handle initial URL params
    loadPostFromUrl();

    // Set filtered posts initially
    setFilteredPosts(safeBlogPosts);

    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchParams, safeBlogPosts]);

  return {
    selectedPost,
    isLoading,
    searchQuery,
    filteredPosts,
    safeBlogPosts,
    handlePostClick,
    handleBackClick,
    handleSearchChange,
    handleClearSearch,
  };
};
